import { useCallback } from 'react';
import { useApiQuery, useApiMutation, type ApiError } from './useApi.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext, useTenantContext } from './contexts.tsx';
import { resolveApiBaseUrl } from './apiFetch.ts';
import { invalidateApiQueries } from './queryKeys.ts';

/**
 * Shared collaboration primitives for regulated/financial/operational
 * detail workbenches: audit trail + comments + attachments, all wired
 * to the cross-module endpoints described by the canonical
 * `entityKind` (e.g. `plm-part`, `ebr-record`, `quality-ncr`).
 *
 * Each hook is enabled by `Boolean(entityId)` so it is safe to mount
 * a detail view before the route param has resolved.
 */

// ─── Audit ────────────────────────────────────────────────────────────────

/**
 * Wire shape returned by `GET /api/v1/audit/{entityKind}/{entityId}`.
 * Mirrors the canonical AuditEnvelope; we accept either the new
 * `entries` field or the older flat array for forward-compat.
 */
export interface AuditTrailEnvelopeEntry {
  id: string;
  occurredAt: string;
  actorId?: string | null;
  actorRole?: string | null;
  action: string;
  previousState?: string | null;
  nextState?: string | null;
  reason?: string | null;
  correlationId?: string | null;
  /** Stored as raw JSON in the backend; the panel handles parsing. */
  evidenceRefsJson?: string | null;
}

export interface AuditTrailEnvelope {
  entries: AuditTrailEnvelopeEntry[];
  total: number;
}

export interface UseAuditTrailOptions {
  enabled?: boolean;
  since?: string;
  limit?: number;
}

/**
 * Fetch the canonical audit chain for an entity.
 *
 * The query key includes `entityKind` + `entityId` so detail panes for
 * different entities don't share cache. The `enabled` flag defaults to
 * `Boolean(entityId)` and is further gated by the caller (commonly the
 * "drawer is open" boolean).
 */
export function useEntityAuditTrail(
  entityKind: string,
  entityId: string | null | undefined,
  options?: UseAuditTrailOptions,
) {
  const params = new URLSearchParams();
  if (options?.since) params.set('since', options.since);
  if (options?.limit != null) params.set('limit', String(options.limit));
  const qs = params.toString();
  const path = entityId
    ? `/audit/${entityKind}/${entityId}${qs ? `?${qs}` : ''}`
    : '';
  return useApiQuery<AuditTrailEnvelope>(path, {
    enabled: Boolean(entityId) && (options?.enabled ?? true),
  });
}

// ─── Comments ─────────────────────────────────────────────────────────────

export interface EntityCommentRecord {
  id: string;
  parentCommentId?: string | null;
  authorDisplayName: string;
  body: string;
  createdAt: string;
  updatedAt?: string | null;
}

export interface EntityCommentsEnvelope {
  comments: EntityCommentRecord[];
}

export interface PostEntityCommentInput {
  body: string;
  parentCommentId?: string;
}

/**
 * Fetch the comment thread for an entity.
 */
export function useEntityComments(
  entityKind: string,
  entityId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const path = entityId ? `/comments/${entityKind}/${entityId}` : '';
  return useApiQuery<EntityCommentsEnvelope>(path, {
    enabled: Boolean(entityId) && (options?.enabled ?? true),
  });
}

/**
 * Post a new comment on the entity. Returns an imperative `mutateAsync`
 * the caller can await, plus a `deleteComment` callback bound to the
 * soft-delete route. The hook invalidates the comments + audit queries
 * for the same entity so the panel refreshes without a manual refetch.
 */
export function useEntityCommentMutations(
  entityKind: string,
  entityId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();
  // Use the per-call descriptor mutation so we never bind to a bogus
  // sentinel path before `entityId` resolves. `postComment` constructs the
  // real route at call time and throws early if `entityId` is still null.
  const postMutation = useApiMutation<EntityCommentRecord>();

  const deleteMutation = useApiMutation<unknown>();

  const postComment = useCallback(
    async (input: PostEntityCommentInput) => {
      if (!entityId) {
        throw new Error('Cannot post a comment without an entityId');
      }
      const result = await postMutation.mutateAsync({
        method: 'POST',
        url: `/comments/${entityKind}/${entityId}`,
        data: input,
      });
      void invalidateApiQueries(queryClient, tenantId, [
        `/comments/${entityKind}/${entityId}`,
        `/audit/${entityKind}/${entityId}`,
      ]);
      return result;
    },
    [postMutation, queryClient, tenantId, entityKind, entityId],
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      const result = await deleteMutation.mutateAsync({
        method: 'DELETE',
        url: `/comments/${commentId}`,
      });
      if (entityId) {
        void invalidateApiQueries(
          queryClient,
          tenantId,
          `/comments/${entityKind}/${entityId}`,
        );
      }
      return result;
    },
    [deleteMutation, queryClient, tenantId, entityKind, entityId],
  );

  return {
    postComment,
    deleteComment,
    isPending: postMutation.isPending || deleteMutation.isPending,
    error: (postMutation.error ?? deleteMutation.error) as ApiError | null,
  };
}

// ─── Attachments ──────────────────────────────────────────────────────────

export interface EntityAttachmentRecord {
  id: string;
  fileName: string;
  contentType?: string | null;
  sizeBytes?: number | null;
  uploadedBy?: string | null;
  uploadedAt: string;
}

export interface EntityAttachmentsEnvelope {
  attachments: EntityAttachmentRecord[];
}

/**
 * Fetch the attachment list for an entity.
 */
export function useEntityAttachments(
  entityKind: string,
  entityId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const path = entityId ? `/attachments/${entityKind}/${entityId}` : '';
  return useApiQuery<EntityAttachmentsEnvelope>(path, {
    enabled: Boolean(entityId) && (options?.enabled ?? true),
  });
}

/**
 * Multipart upload + soft-delete bindings for attachments. The upload
 * helper sends a multipart `file` part (no `Content-Type: application/json`
 * override) and threads the canonical Tyndo auth + tenant headers.
 */
export function useEntityAttachmentMutations(
  entityKind: string,
  entityId: string | null | undefined,
) {
  const { token } = useAuthContext();
  const { tenantId } = useTenantContext();
  const queryClient = useQueryClient();
  const baseUrl = resolveApiBaseUrl();

  const uploadAttachment = useCallback(
    async (file: File): Promise<EntityAttachmentRecord> => {
      if (!entityId) {
        throw new Error('Cannot upload attachment without an entityId');
      }
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(
        `${baseUrl}/attachments/${entityKind}/${entityId}`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
          },
          body: formData,
        },
      );
      if (!res.ok) {
        throw new Error(`Attachment upload failed: ${res.status} ${res.statusText}`);
      }
      const body = (await res.json().catch(() => ({}))) as EntityAttachmentRecord;
      void invalidateApiQueries(queryClient, tenantId, [
        `/attachments/${entityKind}/${entityId}`,
        `/audit/${entityKind}/${entityId}`,
      ]);
      return body;
    },
    [baseUrl, token, tenantId, entityKind, entityId, queryClient],
  );

  const deleteMutation = useApiMutation<unknown>();
  const deleteAttachment = useCallback(
    async (attachmentId: string) => {
      const result = await deleteMutation.mutateAsync({
        method: 'DELETE',
        url: `/attachments/${attachmentId}`,
      });
      if (entityId) {
        void invalidateApiQueries(
          queryClient,
          tenantId,
          `/attachments/${entityKind}/${entityId}`,
        );
      }
      return result;
    },
    [deleteMutation, queryClient, tenantId, entityKind, entityId],
  );

  /**
   * Download an attachment through the Bearer-gated gateway route.
   *
   * The endpoint `GET /api/v1/attachments/download/{id}` requires the
   * `Authorization` + `X-Tenant-Id` headers, which a bare `<a href>` /
   * `window.open` navigation cannot carry — that returns 401. So we fetch
   * the blob with the canonical auth headers, wrap it in an object URL, and
   * drive a synthetic anchor click. The caller passes the file name so the
   * downloaded file is named correctly.
   */
  const downloadAttachment = useCallback(
    async (attachmentId: string, fileName?: string): Promise<void> => {
      const res = await fetch(
        `${baseUrl}/attachments/download/${attachmentId}`,
        {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
          },
        },
      );
      if (!res.ok) {
        throw new Error(
          `Attachment download failed: ${res.status} ${res.statusText}`,
        );
      }
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName ?? attachmentId;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        window.URL.revokeObjectURL(objectUrl);
      }
    },
    [baseUrl, token, tenantId],
  );

  /**
   * @deprecated Returns the raw gateway URL, which is NOT authenticated — a
   * bare `<a href>`/`window.open` navigation carries no Bearer/tenant header
   * and the Bearer-gated download route returns 401. Use `downloadAttachment`
   * instead, which fetches the blob with auth headers and triggers the
   * browser download via an object URL. Retained only for module views still
   * binding `buildAttachmentHref`; those should migrate to `downloadAttachment`.
   */
  const buildDownloadUrl = useCallback(
    (attachmentId: string) =>
      `${baseUrl}/attachments/download/${attachmentId}`,
    [baseUrl],
  );

  return {
    uploadAttachment,
    deleteAttachment,
    downloadAttachment,
    buildDownloadUrl,
    isPending: deleteMutation.isPending,
  };
}

// ─── Aggregate ────────────────────────────────────────────────────────────

/**
 * One-call adapter that returns everything a detail-view side panel
 * needs. The caller passes `auditEnabled` so the audit query is gated
 * on the drawer open state (matches the AuditDrawer UX).
 */
export function useEntityCollaboration(
  entityKind: string,
  entityId: string | null | undefined,
  options?: { auditEnabled?: boolean },
) {
  const audit = useEntityAuditTrail(entityKind, entityId, {
    enabled: options?.auditEnabled ?? false,
  });
  const comments = useEntityComments(entityKind, entityId);
  const attachments = useEntityAttachments(entityKind, entityId);
  const commentMutations = useEntityCommentMutations(entityKind, entityId);
  const attachmentMutations = useEntityAttachmentMutations(entityKind, entityId);
  return {
    audit,
    comments,
    attachments,
    commentMutations,
    attachmentMutations,
  };
}
